import { useEffect, useState } from 'react'
import { User } from '../../../types'
import { List } from '../../../types/state'
import { ListType, SearchEvent } from '../../constants/enums'
import { LISTS, USERSSTATE } from '../../state'
import { idToName, idToSlug } from '../../state/internal/users'
import { pubSubList, pubSubSearch } from '../../state/pubSub'
import { SearchResultUser, searchList } from '../../state/state.search'

const EMPTY_ARRAY = []

// lists can be accessed before data comes in, so we should provide a safe fallback
export const mapListUser = (id: number): User =>
    USERSSTATE.users[id] ?? { id, name: idToName[id], slug: idToSlug[id] }

const setDefaultListUsers = (key: ListType) => {
    const list = LISTS[key]
    if (!list) return EMPTY_ARRAY as Array<User>
    return list.ids().map(mapListUser)
}

const setDefaultListIDs = (key: ListType) => {
    const list = LISTS[key]
    if (!list) return EMPTY_ARRAY as Array<number>
    return list.ids()
}

// Function overloads
export function useList<T extends ListType>(
    key: T,
    options?: {
        returnIDs?: false
        query?: string
        passive?: boolean
        singleUse?: boolean
    },
): Array<User>

export function useList<T extends ListType>(
    key: T,
    options: {
        returnIDs: true
        query?: string
        passive?: boolean
        singleUse?: boolean
    },
): Array<number>

export function useList<T extends ListType>(
    key: T,
    options: {
        returnIDs?: boolean
        query?: string
        passive?: boolean
        singleUse?: boolean
    } = {},
): Array<User> | Array<number> {
    const { returnIDs = false, singleUse, query, passive } = options
    const [searchResults, setSearchResults] = useState<
        Array<User> | Array<number> | null
    >(null)
    const [list, setList] = useState(() =>
        returnIDs ? setDefaultListIDs(key) : setDefaultListUsers(key),
    )
    const [isPopulated, setIsPopulated] = useState(!singleUse)

    useEffect(() => {
        if (!key) return

        // set the list again if the key changes so it's available immediately
        // instead of on next update
        setList(returnIDs ? setDefaultListIDs(key) : setDefaultListUsers(key))

        if (passive || (singleUse && isPopulated)) {
            return
        }

        const callback = (list: List) => {
            // we probably need to ensure the first update is always set
            // so we dont have a case where the list would never load if useList
            // was never called before passiveMode was enabled.
            if (!list) return
            const ids = list.ids()

            if (ids.length > 0) {
                setIsPopulated(true)
            }

            if (returnIDs) {
                setList(ids)
                return
            }

            const users = ids.map(mapListUser)
            setList(users)
        }

        return pubSubList.on(key, callback)
    }, [key, passive, singleUse, isPopulated, returnIDs])

    useEffect(() => {
        // set the list again if the key changes so it's available immediately
        // instead of on next update
        if (key) {
            // this is to ensure we trigger setList once for sure even when
            // passive is enabled
            setList(
                returnIDs ? setDefaultListIDs(key) : setDefaultListUsers(key),
            )
        }
    }, [key, returnIDs])

    useEffect(() => {
        if (!query) {
            setSearchResults(null)
            return
        }

        const callback = (results: Array<SearchResultUser>) => {
            const ids: Array<number> = results.map(({ entry: { id } }) => id)

            if (returnIDs) {
                setSearchResults(ids)
                return
            }

            const users = ids.map(mapListUser)
            setSearchResults(users)
        }

        const pubSubDestroy = pubSubSearch.on(
            `${SearchEvent.RESULTS_LIST}::${key}::${query}`,
            callback,
        )

        searchList({ fields: { name: true }, list: key, query })

        return pubSubDestroy
    }, [key, query, returnIDs])

    return searchResults || (list?.length ? list : EMPTY_ARRAY)
}
